/**
 * Typed query key factory for TanStack Query cache management.
 * Provides hierarchical, type-safe keys for all API data domains.
 */
export const queryKeys = {
  repos: {
    all: ['repos'] as const,
    detail: (name: string) => ['repos', name] as const,
    branches: (name: string) => ['repos', name, 'branches'] as const,
  },
  projects: {
    all: ['projects'] as const,
    detail: (key: string) => ['projects', key] as const,
    repos: (key: string) => ['projects', key, 'repos'] as const,
    activity: (key: string) => ['projects', key, 'activity'] as const,
    openPrs: (key: string) => ['projects', key, 'open-prs'] as const,
    archived: ['projects', 'archived'] as const,
  },
  prs: {
    list: (repoName: string, status?: string) =>
      ['prs', repoName, ...(status ? [status] : [])] as const,
    detail: (repoName: string, prId: string) =>
      ['prs', repoName, prId] as const,
    diff: (repoName: string, prId: string) =>
      ['prs', repoName, prId, 'diff'] as const,
    files: (repoName: string, prId: string) =>
      ['prs', repoName, prId, 'files'] as const,
    checks: (repoName: string, prId: string) =>
      ['prs', repoName, prId, 'checks'] as const,
  },
  comments: {
    byPr: (repoName: string, prId: string) =>
      ['comments', repoName, prId] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    unreadCount: ['notifications', 'unread-count'] as const,
  },
  user: {
    profile: ['user', 'profile'] as const,
    preferences: ['user', 'preferences'] as const,
  },
  configuration: {
    environments: (projectKey: string) =>
      ['configuration', projectKey, 'environments'] as const,
    serviceDefinitions: (projectKey: string) =>
      ['configuration', projectKey, 'service-definitions'] as const,
    servers: (projectKey: string, environmentId: string) =>
      ['configuration', projectKey, environmentId, 'servers'] as const,
    serviceInstances: (projectKey: string, environmentId: string) =>
      ['configuration', projectKey, environmentId, 'service-instances'] as const,
    artifacts: (projectKey: string) =>
      ['configuration', projectKey, 'artifacts'] as const,
    configFiles: (projectKey: string) =>
      ['configuration', projectKey, 'config-files'] as const,
    deploymentArtifacts: (projectKey: string, artifactId: string) =>
      ['configuration', projectKey, artifactId, 'deployment-artifacts'] as const,
    artifactVersions: (projectKey: string, artifactId: string) =>
      ['configuration', projectKey, artifactId, 'versions'] as const,
    versionAssets: (projectKey: string, artifactId: string, version: string) =>
      ['configuration', projectKey, artifactId, 'versions', version, 'assets'] as const,
    configFileOverrides: (projectKey: string, configFileId: string) =>
      ['configuration', projectKey, configFileId, 'overrides'] as const,
    configVersions: (projectKey: string) =>
      ['configuration', projectKey, 'config-versions'] as const,
    databaseDefinitions: (projectKey: string) =>
      ['configuration', projectKey, 'database-definitions'] as const,
    databaseEnvironmentConfigs: (projectKey: string, databaseId: string) =>
      ['configuration', projectKey, databaseId, 'database-environment-configs'] as const,
    databaseScripts: (projectKey: string, databaseId: string) =>
      ['configuration', projectKey, databaseId, 'database-scripts'] as const,
    changedDatabases: (projectKey: string, databaseId: string, environmentId: string) =>
      ['configuration', projectKey, databaseId, environmentId, 'changed-databases'] as const,
  },
  deployments: {
    list: (projectKey: string, page: number, status?: string, excludeTypes?: string, excludeRunIdPrefixes?: string) =>
      ['deployments', projectKey, 'list', page, ...(status ? [status] : []), ...(excludeTypes ? [excludeTypes] : []), ...(excludeRunIdPrefixes ? [excludeRunIdPrefixes] : [])] as const,
    detail: (runId: string) => ['deployments', 'detail', runId] as const,
    artifactStatuses: (projectKey: string, environmentId: string) =>
      ['deployments', projectKey, 'artifact-statuses', environmentId] as const,
    configStatuses: (projectKey: string) =>
      ['deployments', projectKey, 'config-statuses'] as const,
  },
  ci: {
    assetLinks: (repoName: string) => ['ci', repoName, 'asset-links'] as const,
    latestBuild: (repoName: string, assetId: string) =>
      ['ci', repoName, 'latest-build', assetId] as const,
    buildStatusForAsset: (assetId: string) => ['ci', 'build-status', assetId] as const,
    buildStatus: (repoName: string, prId: string) =>
      ['ci', repoName, prId, 'build-status'] as const,
  },
};